import { MedicineTimingRuleType, PillboxItem } from "@/types/pillbox";
import { MemberInfo } from "@/types/member";

// "you"/"your" when viewing your own Pillbox, nickname (or first name)
// otherwise — e.g. "5 new medications found for you." vs "...for Mom."
export function getMemberLabel(
  memberInfo: Pick<MemberInfo, "onBehalf" | "nickname" | "firstName">,
  form: "for" | "possessive" = "for"
): string {
  if (!memberInfo.onBehalf) {
    return form === "possessive" ? "your" : "you";
  }
  const name = memberInfo.nickname || memberInfo.firstName;
  return form === "possessive" ? `${name}'s` : name;
}

export const DAY_FREQUENCY_LABELS: Record<string, string> = {
  ONCE: "Once a day",
  TWICE: "Twice a day",
  THRICE: "3 times a day",
  FOUR_TIMES: "4 times a day",
};

// "Amlodipine" + "5mg" -> "Amlodipine 5mg" (dose baked into the display
// title, matching the Figma card design — name/dose stay separate fields
// in the ledger).
//
// OCR-derived items sometimes already have the dose baked into `name`
// itself (e.g. name: "Metoprolol 12.5mg", dose: "12.5mg") — appending
// `dose` unconditionally in that case doubles it up ("Metoprolol 12.5mg
// 12.5mg"). Skip the append when `name` already ends with `dose`.
export function formatMedicineTitle(
  item: Pick<PillboxItem, "name" | "dose">
): string {
  const name = item.name.trim();
  const dose = item.dose?.trim();
  if (!dose) return name;
  if (name.toLowerCase().endsWith(dose.toLowerCase())) return name;
  return `${name} ${dose}`;
}

export function normalizeMedicineName(name: string): string {
  return (name || "").trim().toLowerCase();
}

// True if `a` and `b` are likely the same medicine "family" — exact match,
// or one name is a word-boundary prefix of the other (e.g. "Crocin" is a
// prefix of "Crocin 650"; "Telma 40" only matches "Telma 40" exactly, not
// "Telma 20" — those are genuinely different medicines, not duplicates).
// Deliberately no hardcoded dose/unit vocabulary (mg/ml/etc.) — brand names
// often append a bare strength number or variant word with no unit at all
// (e.g. "Telma 20", "Crocin Advance"), so this works generically off word
// boundaries instead of guessing at dose-token shapes.
export function isRelatedMedicineName(a: string, b: string): boolean {
  const normA = normalizeMedicineName(a);
  const normB = normalizeMedicineName(b);
  if (!normA || !normB) return false;
  if (normA === normB) return true;
  return normA.startsWith(`${normB} `) || normB.startsWith(`${normA} `);
}

const WEEKDAY_SHORT_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// "Every day" / "Every other day" / "Every 5 days" / "Mon, Wed, Fri" —
// selectedWeekdays (when set) always wins over recurrenceIntervalDays, matching
// the scheduled-view/backend precedence in DoseEventGeneratorService.
function formatCadence(
  item: Pick<PillboxItem, "recurrenceIntervalDays" | "selectedWeekdays">,
  recurrenceIntervalDays?: number
): string {
  if (item.selectedWeekdays && item.selectedWeekdays.length > 0) {
    return item.selectedWeekdays.map((d) => WEEKDAY_SHORT_LABELS[d]).join(", ");
  }
  const interval = recurrenceIntervalDays ?? item.recurrenceIntervalDays ?? 1;
  if (interval <= 1) return "Every day";
  if (interval === 2) return "Every other day";
  return `Every ${interval} days`;
}

// "Every day • Once a day" / "Mon, Wed, Fri • Twice a day" / "As needed"
export function formatFrequencyLine(
  item: Pick<
    PillboxItem,
    | "timingRuleType"
    | "dayFrequency"
    | "frequency"
    | "recurrenceIntervalDays"
    | "selectedWeekdays"
  >,
  recurrenceIntervalDays?: number
): string {
  if (item.timingRuleType === MedicineTimingRuleType.CONDITIONAL) {
    return "As needed";
  }
  const cadence = formatCadence(item, recurrenceIntervalDays);
  const dayFreqLabel = item.dayFrequency
    ? DAY_FREQUENCY_LABELS[item.dayFrequency]
    : undefined;
  return dayFreqLabel ? `${cadence} • ${dayFreqLabel}` : cadence;
}

// Reverse of the fixed morning/afternoon/evening/night slot times — buckets
// an arbitrary "HH:mm" (e.g. from OCR-derived items) back to a daytime card.
export function deriveSlotFromTime(
  time: string
): "morning" | "afternoon" | "evening" | "night" {
  const hour = parseInt(time.split(":")[0], 10);
  if (hour < 12) return "morning";
  if (hour < 16) return "afternoon";
  if (hour < 20) return "evening";
  return "night";
}

function formatTime12h(time: string): string {
  const [hStr, mStr] = time.split(":");
  const hour = parseInt(hStr, 10);
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${mStr}${period}`;
}

// "10:00AM • 8:00PM • 10:00PM"
export function formatSlotTimes(slots?: string[]): string {
  if (!slots || slots.length === 0) return "";
  return slots.map(formatTime12h).join(" • ");
}

// "15 Jun 2026" — startDate/endDate/dateOfVisit are stored as UTC midnight,
// so format in UTC to avoid a local-timezone day shift.
export function formatDisplayDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

// "Dr. Mehta • 12 May 2026" — gracefully drops whichever segment (doctor name /
// visit date) is missing, instead of rendering a blank/dangling-separator
// badge. Falls back to a generic label when OCR couldn't read either (e.g.
// an illegible prescription header).
export function formatDoctorDateBadge(
  doctorName: string | undefined,
  formattedDate: string | undefined,
  fallback: string
): string {
  const parts = [doctorName, formattedDate].filter(Boolean);
  return parts.length > 0 ? parts.join(" • ") : fallback;
}
